<template>
  <header class="svc-topbar">
    <div v-if="loading" class="svc-topbar__status">Cargando...</div>

    <template v-else-if="park">
      <div class="svc-topbar__left">
        <span class="svc-topbar__section">Servicios</span>
        <span class="svc-topbar__park-name">{{ park.name }}</span>
      </div>

      <div class="svc-topbar__divider"></div>

      <div class="svc-topbar__period-wrap">
        <button class="svc-topbar__chevron" @click="shiftPeriod(-1)"><span class="mdi mdi-chevron-left"></span></button>

        <div class="svc-topbar__period-select-wrap">
          <button class="svc-topbar__period-label" @click="pickerOpen = !pickerOpen">{{ currentLabel }}</button>

          <div v-if="pickerOpen" class="svc-topbar__picker">
            <button
              v-for="p in periods"
              :key="p.value"
              class="svc-topbar__picker-item"
              :class="{ 'svc-topbar__picker-item--active': p.value === modelValue, 'svc-topbar__picker-item--future': p.isFuture }"
              @click="selectPeriod(p)"
            >
              {{ p.label }}
              <span v-if="p.status === 'CLOSED'" class="mdi mdi-check-circle svc-topbar__picker-check"></span>
            </button>
          </div>
        </div>

        <button class="svc-topbar__chevron" @click="shiftPeriod(1)"><span class="mdi mdi-chevron-right"></span></button>

        <div class="svc-topbar__status-tag-wrap">
          <span class="svc-topbar__status-tag" :class="`svc-topbar__status-tag--${summary?.status?.toLowerCase()}`" @mouseenter="tagTooltip = true" @mouseleave="tagTooltip = false">
            {{ statusLabel(summary?.status) }}
          </span>
          <div v-if="tagTooltip" class="svc-topbar__tooltip">{{ statusTooltip(summary?.status) }}</div>
        </div>
      </div>

      <div class="svc-topbar__divider"></div>

      <button class="svc-topbar__register-btn" @click="onRegisterClick">
        <span class="mdi mdi-plus"></span> Registrar servicio
      </button>

      <div class="svc-topbar__spacer"></div>

      <div class="svc-topbar__distribute-wrap">
        <button
          class="svc-topbar__distribute-btn"
          :class="distributeButtonClass"
          :disabled="summary?.status !== 'IN_REVIEW'"
          @mouseenter="distributeTooltip = true"
          @mouseleave="distributeTooltip = false"
          @click="onDistributeClick"
        >
          <span v-if="summary?.status === 'CLOSED'" class="mdi mdi-check"></span>
          {{ distributeButtonLabel }}
        </button>
        <div v-if="distributeTooltip && summary?.status === 'PENDING'" class="svc-topbar__tooltip svc-topbar__tooltip--right">
          Faltan {{ summary.pendingCount }} servicio{{ summary.pendingCount === 1 ? '' : 's' }} por completar antes de poder distribuir.
        </div>
      </div>

      <div class="svc-topbar__divider"></div>

      <GlobalAlertsChip :park-id="park?.id" />
      <NotificationBell :park-id="park?.id" />
    </template>
  </header>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import NotificationBell from '@/components/Global/NotificationBell.vue'
import GlobalAlertsChip from '@/components/Global/GlobalAlertsChip.vue'
import { getServicePeriodsList, getServicePeriodSummary } from '@/services/services'

const props = defineProps({
  park: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  alerts: { type: Array, default: () => [] },
  modelValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'register-service', 'distribute', 'open-notifications'])

const periods = ref([])
const summary = ref(null)
const pickerOpen = ref(false)
const tagTooltip = ref(false)
const distributeTooltip = ref(false)

async function loadPeriods() {
  if (!props.park) return
  periods.value = await getServicePeriodsList(props.park.id)
  if (!props.modelValue) {
    const nonFuture = periods.value.filter((p) => !p.isFuture)
    const current = nonFuture[nonFuture.length - 1] || periods.value[0]
    emit('update:modelValue', current.value)
  }
}

async function loadSummary() {
  if (!props.park || !props.modelValue) return
  summary.value = await getServicePeriodSummary(props.park.id, props.modelValue)
}

defineExpose({ reloadSummary: loadSummary })

watch(() => props.park, loadPeriods, { immediate: true })
watch(() => props.modelValue, loadSummary, { immediate: true })

const currentLabel = computed(() => periods.value.find((p) => p.value === props.modelValue)?.label || '—')

function shiftPeriod(delta) {
  const idx = periods.value.findIndex((p) => p.value === props.modelValue)
  const next = periods.value[idx + delta]
  if (next) emit('update:modelValue', next.value)
}

function selectPeriod(p) {
  pickerOpen.value = false
  emit('update:modelValue', p.value)
}

function statusLabel(s) {
  return { PENDING: 'Pendiente', IN_REVIEW: 'En revisión', DISTRIBUTED: 'Distribuido', CLOSED: 'Cerrado' }[s] || ''
}

function statusTooltip(s) {
  return {
    PENDING: 'Todavía faltan servicios por registrar este mes.',
    IN_REVIEW: 'Todos los servicios registrados. Verifica la distribución y ejecuta el cierre cuando estés listo.',
    DISTRIBUTED: 'Ya se distribuyó. Los cargos están en proceso de transferencia a cobranza.',
    CLOSED: 'Proceso completo. Este mes ya está cerrado.',
  }[s] || ''
}

const distributeButtonLabel = computed(() => (summary.value?.status === 'CLOSED' ? 'Mes cerrado' : 'Distribuir y transferir'))
const distributeButtonClass = computed(() => {
  if (summary.value?.status === 'CLOSED') return 'svc-topbar__distribute-btn--closed'
  if (summary.value?.status === 'IN_REVIEW') return 'svc-topbar__distribute-btn--active'
  return 'svc-topbar__distribute-btn--disabled'
})

function onRegisterClick() {
  if (summary.value?.status === 'CLOSED') {
    if (!confirm('Este mes ya está cerrado. Agregar un servicio requerirá reabrir el mes y redistribuir. ¿Continuar?')) return
  }
  emit('register-service', summary.value?.periodId)
}

function onDistributeClick() {
  if (summary.value?.status !== 'IN_REVIEW') return
  emit('distribute', summary.value.periodId)
}
</script>

<style scoped>
.svc-topbar {
  display: flex;
  align-items: center;
  padding: 0.6rem 1.5rem;
  background: var(--side-panel);
  border-left: 1px solid var(--border-subtle);
  gap: 0.75rem;
  height: 56px;
  box-sizing: border-box;
}

.svc-topbar__status { font-size: 0.85rem; color: var(--text-secondary); }

.svc-topbar__divider { width: 1px; align-self: stretch; background: rgba(255, 255, 255, 0.15); flex-shrink: 0; }
.svc-topbar__spacer { flex: 1; }

.svc-topbar__left { display: flex; flex-direction: column; flex-shrink: 0; }
.svc-topbar__section { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #ffffff; }
.svc-topbar__park-name { font-family: var(--font-body); font-size: 11px; color: rgba(255, 255, 255, 0.6); }

.svc-topbar__period-wrap { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }

.svc-topbar__chevron {
  background: none; border: none; color: rgba(255, 255, 255, 0.7); cursor: pointer; display: flex; font-size: 14px;
}

.svc-topbar__period-select-wrap { position: relative; }

.svc-topbar__period-label {
  background: none; border: none; color: #ffffff; font-family: var(--font-body); font-size: 14px; font-weight: 500; cursor: pointer;
}

.svc-topbar__picker {
  position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15); padding: 0.4rem; z-index: 200;
  display: grid; grid-template-columns: 1fr 1fr; gap: 0.2rem; min-width: 220px;
}

.svc-topbar__picker-item {
  display: flex; align-items: center; justify-content: space-between; gap: 0.3rem;
  background: none; border: none; border-radius: 5px; padding: 0.4rem 0.5rem;
  font-family: var(--font-body); font-size: 12px; color: var(--text-content); cursor: pointer; text-align: left;
}

.svc-topbar__picker-item:hover { background: #f8fafc; }
.svc-topbar__picker-item--active { background: #eff6ff; color: #3b82f6; font-weight: 500; }
.svc-topbar__picker-item--future { color: #cbd5e1; }
.svc-topbar__picker-check { color: #16a34a; font-size: 12px; }

.svc-topbar__status-tag-wrap { position: relative; margin-left: 0.75rem; }

.svc-topbar__status-tag {
  border-radius: 20px; padding: 0.25rem 0.65rem; font-family: var(--font-body); font-size: 11px; font-weight: 500; cursor: default;
}

.svc-topbar__status-tag--pending { background: #374151; color: #d1d5db; }
.svc-topbar__status-tag--in_review { background: #92400e; color: #fde68a; }
.svc-topbar__status-tag--distributed { background: #1e40af; color: #bfdbfe; }
.svc-topbar__status-tag--closed { background: #14532d; color: #bbf7d0; }

.svc-topbar__tooltip {
  position: absolute; top: calc(100% + 6px); left: 0; background: #1e3a5f; color: #ffffff;
  font-family: var(--font-body); font-size: 11px; padding: 0.5rem 0.65rem; border-radius: 8px;
  min-width: 220px; z-index: 200; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.svc-topbar__tooltip--right { left: auto; right: 0; }

.svc-topbar__register-btn {
  display: flex; align-items: center; gap: 0.3rem; height: 32px; background: #24487a;
  border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; padding: 0 0.65rem;
  color: #ffffff; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer;
  flex-shrink: 0; transition: background 100ms ease;
}

.svc-topbar__register-btn:hover { background: #2d5a9e; }

.svc-topbar__distribute-wrap { position: relative; flex-shrink: 0; }

.svc-topbar__distribute-btn {
  height: 32px; border-radius: 6px; padding: 0 1rem; font-family: var(--font-body); font-size: 13px; font-weight: 500;
  border: none; cursor: pointer; transition: all 300ms ease;
}

.svc-topbar__distribute-btn--disabled {
  background: rgba(255, 255, 255, 0.2); color: rgba(255, 255, 255, 0.4); cursor: not-allowed;
}

.svc-topbar__distribute-btn--active {
  background: #ffffff; color: #1e3a5f; box-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
}

.svc-topbar__distribute-btn--closed {
  background: #14532d; color: #bbf7d0; cursor: default; display: flex; align-items: center; gap: 0.3rem;
}

.svc-topbar__notif {
  position: relative; background: none; border: none; color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem; cursor: pointer; display: flex; flex-shrink: 0;
}

.svc-topbar__notif-badge {
  position: absolute; top: -6px; right: -8px; background: var(--accent-blue); color: white;
  font-size: 0.6rem; font-family: var(--font-mono); padding: 0 4px; border-radius: 8px; min-width: 14px; text-align: center;
}
</style>